import React, { useEffect, useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { CtaOverlayButton, LpSection, UpdateSectionOverlayData } from '../../services/api/lpGenerationService';
import CtaOverlayCanvas from './CtaOverlayCanvas';
import SrOnlyTextField from './SrOnlyTextField';

interface OverlaySectionEditorProps {
  section: LpSection;
  sectionIndex: number;
  onChange: (data: UpdateSectionOverlayData) => void;
  onImageEdit?: (sectionIndex: number) => void;
}

const DEFAULT_BUTTON: CtaOverlayButton = {
  x_pct: 10,
  y_pct: 82,
  w_pct: 80,
  h_pct: 12,
  href: '#cta-final',
  label: 'CTA',
  modal_id: null,
};

const OverlaySectionEditor: React.FC<OverlaySectionEditorProps> = ({ section, sectionIndex, onChange, onImageEdit }) => {
  const initialButton = useMemo(
    () => section.cta_overlay?.buttons?.[0] ?? { ...DEFAULT_BUTTON, label: section.title || 'CTA' },
    [section],
  );
  const [button, setButton] = useState<CtaOverlayButton>(initialButton);
  const [srOnlyText, setSrOnlyText] = useState(`${section.title} ${section.content}`.trim());

  useEffect(() => {
    setButton(initialButton);
    setSrOnlyText(section.sr_only_text ?? `${section.title} ${section.content}`.trim());
  }, [initialButton, section.content, section.sr_only_text, section.title]);

  const updateButton = (nextButton: CtaOverlayButton) => {
    setButton(nextButton);
    onChange({ cta_overlay: { buttons: [nextButton] } });
  };

  const updateSrOnlyText = (value: string) => {
    setSrOnlyText(value);
    onChange({ sr_only_text: value });
  };

  const updateNumber = (field: keyof Pick<CtaOverlayButton, 'x_pct' | 'y_pct' | 'w_pct' | 'h_pct'>, value: string) => {
    updateButton({ ...button, [field]: Number(value) });
  };

  return (
    <div className="rounded-lg border border-warm-border bg-white p-4">
      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <CtaOverlayCanvas
          imageUrl={section.image_url}
          title={section.title}
          button={button}
          onChange={updateButton}
        />
        <div className="space-y-4">
          {section.image_url && onImageEdit && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onImageEdit(sectionIndex)}
                className="inline-flex items-center gap-1.5 rounded-md border border-warm-border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <Pencil className="h-4 w-4" />
                画像を編集
              </button>
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            {(['x_pct', 'y_pct', 'w_pct', 'h_pct'] as const).map((field) => {
              const label = field === 'x_pct' ? 'X' : field === 'y_pct' ? 'Y' : field === 'w_pct' ? 'W' : 'H';
              return (
                <div key={field}>
                  <label htmlFor={`overlay-${field}`} className="block text-xs font-medium text-gray-600 mb-1">
                    {label}
                  </label>
                  <input
                    id={`overlay-${field}`}
                    type="number"
                    min={0}
                    max={100}
                    value={button[field]}
                    onChange={(event) => updateNumber(field, event.target.value)}
                    className="w-full rounded-md border border-warm-border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="overlay-href" className="block text-xs font-medium text-gray-600 mb-1">リンク</label>
              <input
                id="overlay-href"
                type="text"
                value={button.href}
                onChange={(event) => updateButton({ ...button, href: event.target.value })}
                className="w-full rounded-md border border-warm-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="overlay-label" className="block text-xs font-medium text-gray-600 mb-1">ラベル</label>
              <input
                id="overlay-label"
                type="text"
                value={button.label}
                onChange={(event) => updateButton({ ...button, label: event.target.value })}
                className="w-full rounded-md border border-warm-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <SrOnlyTextField
            value={srOnlyText}
            onChange={updateSrOnlyText}
          />
        </div>
      </div>
    </div>
  );
};

export default OverlaySectionEditor;
